import React from 'react';

const KoryuScrollBanner = ({ size = 'md' }) => {
  const sizes = {
    sm: { width: 60, height: 160, fontSize: '16px', letterSpacing: '5px' },
    md: { width: 80, height: 200, fontSize: '20px', letterSpacing: '7px' },
    lg: { width: 100, height: 250, fontSize: '24px', letterSpacing: '9px' }
  };
  
  const s = sizes[size];
  
  const floatStyle = `
    @keyframes koryuFloat {
      0%, 100% { transform: translateY(0px) rotate(-1deg); }
      50% { transform: translateY(-6px) rotate(1deg); }
    }
  `;
  
  return (
    <div 
      className="relative inline-block"
      style={{
        width: s.width,
        height: s.height,
        filter: 'drop-shadow(4px 6px 8px rgba(0,0,0,0.4)) drop-shadow(0px 2px 4px rgba(0,0,0,0.2))',
        animation: 'koryuFloat 3s ease-in-out infinite'
      }}
    >
      <style>{floatStyle}</style>
      
      <svg 
        viewBox="0 0 80 200" 
        className="w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="paperGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F5E6D3" />
            <stop offset="15%" stopColor="#FDF8F0" />
            <stop offset="50%" stopColor="#FFFDF9" />
            <stop offset="85%" stopColor="#FDF8F0" />
            <stop offset="100%" stopColor="#E8D4B8" />
          </linearGradient>
          
          <linearGradient id="woodGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#8B5A2B" />
            <stop offset="30%" stopColor="#A0522D" />
            <stop offset="70%" stopColor="#8B4513" />
            <stop offset="100%" stopColor="#654321" />
          </linearGradient>
          
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#D4AF37" />
            <stop offset="50%" stopColor="#FFD700" />
            <stop offset="100%" stopColor="#B8860B" />
          </linearGradient>
        </defs>
        
        {/* Top wooden roll - 3D effect */}
        <ellipse cx="40" cy="10" rx="36" ry="8" fill="#4A3728" />
        <rect x="4" y="4" width="72" height="12" rx="6" fill="url(#woodGradient)" />
        <ellipse cx="40" cy="4" rx="36" ry="4" fill="#A0522D" />
        <ellipse cx="40" cy="4" rx="32" ry="3" fill="#8B5A2B" opacity="0.5" />
        
        {/* Decorative gold caps on top roll */}
        <circle cx="8" cy="8" r="5" fill="url(#goldGradient)" />
        <circle cx="8" cy="8" r="3" fill="#FFD700" opacity="0.6" />
        <circle cx="72" cy="8" r="5" fill="url(#goldGradient)" />
        <circle cx="72" cy="8" r="3" fill="#FFD700" opacity="0.6" />
        
        {/* Main scroll paper */}
        <path
          d="M 10 14 Q 8 14, 8 16 L 8 180 Q 8 182, 10 182 L 70 182 Q 72 182, 72 180 L 72 16 Q 72 14, 70 14 Z"
          fill="url(#paperGradient)"
          stroke="url(#goldGradient)"
          strokeWidth="1.5"
        />
        
        {/* Paper texture - subtle aged lines */}
        <g opacity="0.15">
          <line x1="12" y1="30" x2="68" y2="30" stroke="#8B7355" strokeWidth="0.5" />
          <line x1="12" y1="50" x2="68" y2="50" stroke="#8B7355" strokeWidth="0.5" />
          <line x1="12" y1="70" x2="68" y2="70" stroke="#8B7355" strokeWidth="0.5" />
          <line x1="12" y1="130" x2="68" y2="130" stroke="#8B7355" strokeWidth="0.5" />
          <line x1="12" y1="150" x2="68" y2="150" stroke="#8B7355" strokeWidth="0.5" />
        </g>
        
        {/* Aged paper spots for authenticity */}
        <g opacity="0.08">
          <circle cx="20" cy="40" r="3" fill="#8B7355" />
          <circle cx="55" cy="90" r="2" fill="#8B7355" />
          <circle cx="25" cy="140" r="2.5" fill="#8B7355" />
          <circle cx="60" cy="160" r="1.5" fill="#8B7355" />
        </g>
        
        {/* Bottom wooden roll - 3D effect */}
        <rect x="4" y="180" width="72" height="12" rx="6" fill="url(#woodGradient)" />
        <ellipse cx="40" cy="192" rx="36" ry="8" fill="#4A3728" />
        <ellipse cx="40" cy="186" rx="36" ry="4" fill="#654321" />
        
        {/* Decorative gold caps on bottom roll */}
        <circle cx="8" cy="188" r="5" fill="url(#goldGradient)" />
        <circle cx="8" cy="188" r="3" fill="#FFD700" opacity="0.6" />
        <circle cx="72" cy="188" r="5" fill="url(#goldGradient)" />
        <circle cx="72" cy="188" r="3" fill="#FFD700" opacity="0.6" />
        
        {/* Red seal/stamp at top */}
        <circle cx="40" cy="4" r="4" fill="#8B0000" />
        <circle cx="40" cy="4" r="2.5" fill="#B22222" />
        
        {/* Hanging cord */}
        <path
          d="M 36 0 Q 32 -8, 40 -12 Q 48 -8, 44 0"
          fill="none"
          stroke="#8B0000"
          strokeWidth="2"
        />
        <circle cx="40" cy="-12" r="3" fill="#FFD700" />
      </svg>
      
      {/* Japanese text overlay */}
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{ 
          writingMode: 'vertical-rl',
          textOrientation: 'upright',
          paddingTop: '25px',
          paddingBottom: '25px'
        }}
      >
        <span 
          style={{ 
            fontSize: s.fontSize,
            fontWeight: '700',
            fontFamily: "'Noto Serif JP', 'Yu Mincho', serif",
            color: '#2D1810',
            letterSpacing: s.letterSpacing,
            textShadow: '1px 1px 0px rgba(255,255,255,0.5), -0.5px -0.5px 0px rgba(0,0,0,0.1)'
          }}
        >
          古流武術
        </span>
      </div>
    </div>
  );
};

export default KoryuScrollBanner;
